/**
 * Staging Area Feature - Helper Functions (Phase 3)
 *
 * Provides core functionality for staging area workflow where aircraft are
 * temporarily placed in staging zones for servicing operations before/after
 * their permanent parking spots.
 *
 * Typical workflow:
 * 1. Aircraft lands → Staging area (30-60 min for unloading/servicing)
 * 2. Staging → Permanent spot (main dwell time)
 * 3. Permanent → Staging area (30-60 min for departure prep)
 * 4. Staging → Takeoff
 */

import { ParkingRequest, Spot, MINUTES_PER_BUCKET } from "./types";

/**
 * Determines if a spot is located in a staging zone
 *
 * Checks the spot's shape metadata for the `is_staging` flag, which is the
 * single source of truth for staging zone designation. This flag is set in
 * the database and accessed via `spot.shape.metadata.is_staging`.
 *
 * @param spot - The spot to check
 * @returns true if spot is in a staging zone, false otherwise
 *
 * @example
 * ```typescript
 * const stagingSpot = {
 *   id: "S1",
 *   shape: { metadata: { is_staging: true } }
 * };
 * isSpotInStagingZone(stagingSpot); // true
 *
 * const permanentSpot = {
 *   id: "A1",
 *   shape: { metadata: { is_staging: false } }
 * };
 * isSpotInStagingZone(permanentSpot); // false
 * ```
 *
 * **Design notes:**
 * - Returns false for missing/undefined metadata (safe default)
 * - Pairs with `request.isStagingSegment` (request-side constraint)
 * - No duplication: reads directly from shape.metadata (single source of truth)
 */
export function isSpotInStagingZone(spot: Spot): boolean {
  return spot.shape?.metadata?.is_staging === true;
}

/**
 * Staging configuration result
 */
export type StagingConfig = {
  useArrival: boolean;
  useDeparture: boolean;
};

/**
 * Determines if an aircraft should use arrival and/or departure staging
 *
 * Aircraft use staging when ALL conditions are met:
 * 1. Staging is enabled for that direction (arrival/departure)
 * 2. Dwell time exceeds the quick turn threshold
 * 3. Dwell time is sufficient for requested staging periods
 *
 * Quick turns (short visits) bypass staging to avoid unnecessary moves.
 *
 * @param request - The parking request to evaluate
 * @param enableArrivalStaging - Enable arrival staging workflow
 * @param enableDepartureStaging - Enable departure staging workflow
 * @param quickTurnThresholdMinutes - Minimum dwell time for staging (minutes)
 * @param arrivalStagingDwellMinutes - Time for arrival staging (minutes)
 * @param departureStagingDwellMinutes - Time for departure staging (minutes)
 * @returns Object with useArrival and useDeparture booleans
 *
 * @example
 * ```typescript
 * // 10-hour stay with both staging enabled
 * const longStay = { arrivalBucket: 0, departureBucket: 40 }; // 10 hours
 * shouldUseStaging(longStay, true, true, 120, 60, 30);
 * // → { useArrival: true, useDeparture: true }
 *
 * // 90-minute quick turn: bypass all staging
 * const quickTurn = { arrivalBucket: 0, departureBucket: 6 }; // 90 min
 * shouldUseStaging(quickTurn, true, true, 120, 60, 30);
 * // → { useArrival: false, useDeparture: false }
 *
 * // Arrival only enabled
 * shouldUseStaging(longStay, true, false, 120, 60, 30);
 * // → { useArrival: true, useDeparture: false }
 *
 * // Insufficient time (need 90 min, only have 80 min)
 * const tooShort = { arrivalBucket: 0, departureBucket: 5 }; // 75 min
 * shouldUseStaging(tooShort, true, true, 60, 60, 30);
 * // → { useArrival: false, useDeparture: false }
 * ```
 *
 * **Time bucket conversion:**
 * - Each bucket = MINUTES_PER_BUCKET (15 minutes)
 * - Buckets 0-40 = 10 hours (40 × 15 = 600 min)
 * - Threshold 120 min = 8 buckets (120 ÷ 15)
 */
export function shouldUseStaging(
  request: ParkingRequest,
  enableArrivalStaging: boolean,
  enableDepartureStaging: boolean,
  quickTurnThresholdMinutes: number,
  arrivalStagingDwellMinutes: number,
  departureStagingDwellMinutes: number
): StagingConfig {
  // Calculate dwell time in minutes
  const dwellBuckets = request.departureBucket - request.arrivalBucket;
  const dwellMinutes = dwellBuckets * MINUTES_PER_BUCKET;

  // Quick turn bypass - below threshold
  if (dwellMinutes <= quickTurnThresholdMinutes) {
    return { useArrival: false, useDeparture: false };
  }

  // Calculate minimum required time based on enabled staging
  const requiredMinutes =
    (enableArrivalStaging ? arrivalStagingDwellMinutes : 0) +
    (enableDepartureStaging ? departureStagingDwellMinutes : 0);

  // Insufficient dwell time for requested staging
  if (dwellMinutes < requiredMinutes) {
    return { useArrival: false, useDeparture: false };
  }

  // Return configuration based on enabled flags
  return {
    useArrival: enableArrivalStaging,
    useDeparture: enableDepartureStaging,
  };
}

/**
 * Creates 3-segment staging workflow for an aircraft
 *
 * Splits a single request into 3 temporal segments:
 * 1. **Arrival staging** - Aircraft lands, goes to staging for servicing
 * 2. **Permanent spot** - Main dwell time in permanent parking
 * 3. **Departure staging** - Return to staging for departure prep
 *
 * Each staging segment is marked with `isStagingSegment: true`, which
 * constrains it to staging zones during placement.
 *
 * @param request - The original parking request
 * @param stagingDwellBuckets - Time to spend in staging area (in 15-min buckets)
 * @returns Array of 3 parking requests (staging → permanent → staging)
 *
 * @example
 * ```typescript
 * const request = {
 *   id: "AC1",
 *   sizeCategory: "small",
 *   arrivalBucket: 0,    // 00:00
 *   departureBucket: 40, // 10:00 (10 hours)
 *   tail_number: "N123AB"
 * };
 *
 * const segments = createStagingSegments(request, 3); // 45 min = 3 buckets
 *
 * // Result:
 * // segments[0]: buckets 0-3,   isStagingSegment: true  (arrival staging)
 * // segments[1]: buckets 3-37,  isStagingSegment: false (permanent, 8h 30m)
 * // segments[2]: buckets 37-40, isStagingSegment: true  (departure staging)
 * ```
 *
 * **Important:**
 * - All original request properties are preserved (id, aircraft, tail_number, etc.)
 * - Time buckets are recalculated for each segment
 * - Middle segment gets remaining time after subtracting both staging periods
 * - Staging segments receive special treatment in algorithm (zero waste penalty, move exemption)
 *
 * **Time calculation:**
 * ```
 * Original:     |<-------------- 40 buckets (10 hours) -------------->|
 * Segment 1:    |<- 3 ->|                                               (staging)
 * Segment 2:            |<----------- 34 buckets (8h 30m) -------->|   (permanent)
 * Segment 3:                                                        |<- 3 ->|  (staging)
 * ```
 */
export function createStagingSegments(
  request: ParkingRequest,
  stagingDwellBuckets: number
): ParkingRequest[] {
  const { arrivalBucket, departureBucket } = request;

  // Validate sufficient dwell time for 2× staging periods
  const totalBuckets = departureBucket - arrivalBucket;
  const requiredBuckets = stagingDwellBuckets * 2;

  if (totalBuckets < requiredBuckets) {
    const totalMinutes = totalBuckets * MINUTES_PER_BUCKET;
    const requiredMinutes = requiredBuckets * MINUTES_PER_BUCKET;
    throw new Error(
      `Insufficient dwell time for staging workflow: aircraft ${request.tail_number} ` +
      `has ${totalMinutes} min available, but ${requiredMinutes} min required ` +
      `(2× ${stagingDwellBuckets * MINUTES_PER_BUCKET} min staging periods). ` +
      `This should have been caught by shouldUseStaging().`
    );
  }

  // Segment 1: Arrival staging
  const arrivalStagingEnd = arrivalBucket + stagingDwellBuckets;

  // Segment 2: Permanent spot (middle)
  const permanentStart = arrivalStagingEnd;
  const permanentEnd = departureBucket - stagingDwellBuckets;

  // Segment 3: Departure staging
  const departureStagingStart = permanentEnd;

  return [
    // Arrival staging segment
    {
      ...request,
      arrivalBucket: arrivalBucket,
      departureBucket: arrivalStagingEnd,
      isStagingSegment: true,
    },
    // Permanent segment (no staging flag)
    {
      ...request,
      arrivalBucket: permanentStart,
      departureBucket: permanentEnd,
      isStagingSegment: undefined, // Explicit undefined for clarity
    },
    // Departure staging segment
    {
      ...request,
      arrivalBucket: departureStagingStart,
      departureBucket: departureBucket,
      isStagingSegment: true,
    },
  ];
}
